<script lang="ts">
  import { det, iterations, monteCarloResult, runMonteCarloNow } from "../lib/stores";
  import { fmt1 } from "../lib/format";
  import type { MonteCarloResult } from "../lib/types";

  interface Bar { heightPct: number; inrange: boolean; title: string }
  interface TickMark { leftPct: number; label: string }

  let bars: Bar[] = [];
  let ticks: TickMark[] = [];
  let flatMessage = "";
  let running = false;

  function run() {
    running = true;
    runMonteCarloNow();
    running = false;
  }

  $: buildHistogram($monteCarloResult);

  function buildHistogram(result: MonteCarloResult | null) {
    if (!result || !result.samples.length) { bars = []; ticks = []; flatMessage = ""; return; }
    const samples = result.samples;
    const trueMin = samples[0], trueMax = samples[samples.length - 1];
    if (trueMax - trueMin < 0.05) {
      bars = [];
      ticks = [];
      flatMessage = `No spread yet — P10 = P50 = P90 = ${fmt1(result.p50)}d (every O/M/P still equal)`;
      return;
    }
    flatMessage = "";
    const min = trueMin, max = trueMax;
    const bins = 28;
    const width = (max - min) / bins;
    const counts = new Array(bins).fill(0);
    samples.forEach((v) => {
      const b = Math.min(bins - 1, Math.floor((v - min) / width));
      counts[b]++;
    });
    const maxCount = Math.max(...counts);
    bars = counts.map((c, i) => {
      const binLo = min + i * width, binHi = binLo + width;
      return {
        heightPct: maxCount ? (c / maxCount) * 100 : 0,
        inrange: binHi >= result.p10 && binLo <= result.p90,
        title: `${fmt1(binLo)}–${fmt1(binHi)}d: ${c} runs`,
      };
    });
    ticks = [
      { v: min, l: fmt1(min) + "d" },
      { v: result.p10, l: "P10 " + fmt1(result.p10) + "d" },
      { v: result.p50, l: "P50 " + fmt1(result.p50) + "d" },
      { v: result.p90, l: "P90 " + fmt1(result.p90) + "d" },
      { v: max, l: fmt1(max) + "d" },
    ].map((m) => ({ leftPct: ((m.v - min) / (max - min)) * 100, label: m.l }));
  }
</script>

<section>
  <div class="section-head">
    <div class="section-title">Monte Carlo simulation — true P10/P50/P90</div>
    <div class="section-actions">
      <label class="mono" style="font-size:11px;color:var(--text-secondary);display:flex;align-items:center;gap:6px;">
        iterations
        <input type="number" bind:value={$iterations} min="200" max="20000" step="100"
          style="width:70px;font-family:'IBM Plex Mono',monospace;padding:4px 6px;border:1px solid var(--input-border);border-radius:5px;background:var(--input-bg);color:var(--text-primary);" />
      </label>
      <button class="btn primary" type="button" disabled={running || !$det.ok} on:click={run}>Run simulation</button>
    </div>
  </div>
  <div class="chart-frame">
    <div id="histFrame">
      {#if flatMessage}
        <div class="hbar inrange" style="height:100%; flex:0 0 60px; margin:0 auto;"></div>
      {:else}
        {#each bars as bar}
          <div class="hbar" class:inrange={bar.inrange} style="height:{bar.heightPct}%" title={bar.title}></div>
        {/each}
      {/if}
    </div>
    <div class="hist-scale" id="histScale">
      {#if flatMessage}
        <div class="hist-tick" style="left:50%">{flatMessage}</div>
      {:else}
        {#each ticks as tick}
          <div class="hist-tick" style="left:{tick.leftPct}%">{tick.label}</div>
        {/each}
      {/if}
    </div>
    <div class="legend" id="mcLegend" style="margin-top:14px;">
      {#if $monteCarloResult}
        <div class="legend-item"><span class="legend-swatch" style="background:var(--s-event)"></span>80% of simulated outcomes (P10–P90)</div>
        <div class="legend-item"><span class="legend-swatch" style="background:var(--gridline)"></span>outside the 80% band</div>
      {/if}
    </div>
  </div>
</section>
